import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Send, Image, X, Users, User, MessageSquare, Heart, Video, Reply, Search } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import useWebSocket from "../hooks/useWebSocket";
import MessageBubble from "./MessageBubble";
import ImageViewer from "./ImageViewer";
import VideoViewer from "./VideoViewer";
import GroupsManager from "./GroupsManager";
import GroupChat from "./GroupChat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatTab({ user }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [chatMode, setChatMode] = useState("groups"); // Changed from "group" to "groups"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null); // NEW: for group chat view
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const { lastMessage } = useWebSocket(user);
  
  const isParent = user?.role === "parent";
  const isCoach = user?.role === "coach";

  useEffect(() => {
    fetchMessages();
    fetchUsers();
  }, [chatMode]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === "new_message") {
      const msg = lastMessage.data;
      if (chatMode === "group" && !msg.is_private && !msg.is_parent_chat) {
        setMessages(prev => {
          if (prev.find(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
      } else if (chatMode === "parents" && msg.is_parent_chat) {
        setMessages(prev => {
          if (prev.find(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
      }
    } else if (lastMessage && lastMessage.type === "reaction_update") {
      setMessages(prev => prev.map(m => 
        m.message_id === lastMessage.data.message_id 
          ? { ...m, reactions: lastMessage.data.reactions }
          : m
      ));
    } else if (lastMessage && lastMessage.type === "message_deleted") {
      setMessages(prev => prev.filter(m => m.message_id !== lastMessage.data.message_id));
    }
  }, [lastMessage, chatMode]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      let endpoint = `${API}/messages`;
      
      if (chatMode === "parents") {
        endpoint = `${API}/messages/parents`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      if (chatMode !== "parents") {
        toast.error("Ошибка загрузки сообщений");
      }
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      let filteredUsers = response.data.filter(u => u.user_id !== user?.user_id);
      if (isParent) {
        filteredUsers = filteredUsers.filter(u => u.role === "coach");
      }
      setUsers(filteredUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    const tempId = `temp_${Date.now()}`;
    const isVideoFile = selectedFile?.type?.startsWith("video/");
    
    // Optimistic update - show message immediately
    const optimisticMessage = {
      message_id: tempId,
      sender_id: user?.user_id,
      sender_name: `${user?.first_name} ${user?.last_name}`,
      content: newMessage.trim() || (isVideoFile ? "🎬 Видео" : "📷 Фото"),
      image_url: selectedFile && !isVideoFile ? URL.createObjectURL(selectedFile) : null,
      media_url: selectedFile && isVideoFile ? URL.createObjectURL(selectedFile) : null,
      reactions: {},
      created_at: new Date().toISOString(),
      is_private: false,
      is_parent_chat: chatMode === "parents",
      reply_to: replyTo ? { sender_name: replyTo.sender_name, content: replyTo.content } : null,
      _pending: true
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage("");
    setReplyTo(null);
    const fileToUpload = selectedFile;
    setSelectedFile(null);

    try {
      const token = localStorage.getItem("token");
      let mediaUrl = null;

      if (fileToUpload) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", fileToUpload);
        
        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          withCredentials: true
        });
        mediaUrl = uploadResponse.data.file_url;
        setUploading(false);
      }

      const response = await axios.post(`${API}/messages`, {
        content: optimisticMessage.content,
        image_url: !isVideoFile ? mediaUrl : null,
        media_url: isVideoFile ? mediaUrl : null,
        recipient_id: null,
        is_parent_chat: chatMode === "parents",
        reply_to_id: replyTo?.message_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      // Replace optimistic message with real one
      setMessages(prev => prev.map(m => 
        m.message_id === tempId ? response.data : m
      ));
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      toast.error("Ошибка отправки сообщения");
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Файл слишком большой (макс. 50MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    // Optimistic update for instant feel
    setMessages(prev => prev.map(m => {
      if (m.message_id !== messageId) return m;
      
      const reactions = { ...m.reactions };
      if (!reactions[emoji]) {
        reactions[emoji] = [];
      }
      
      const userId = user?.user_id;
      if (reactions[emoji].includes(userId)) {
        reactions[emoji] = reactions[emoji].filter(id => id !== userId);
        if (reactions[emoji].length === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji] = [...reactions[emoji], userId];
      }
      
      return { ...m, reactions };
    }));

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/messages/reaction`, {
        message_id: messageId,
        emoji: emoji
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      // Revert on error
      fetchMessages();
      toast.error("Ошибка");
    }
  };

  const handleDelete = async (messageId) => {
    // Optimistic delete
    setMessages(prev => prev.filter(m => m.message_id !== messageId));
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      toast.error("Ошибка удаления");
      fetchMessages();
    }
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  const getChatModes = () => {
    if (isParent) {
      return [
        { id: "parents", label: "Чат родителей", icon: Heart },
        { id: "private", label: "Тренеры", icon: User }
      ];
    }
    if (isCoach) {
      return [
        { id: "groups", label: "Группы", icon: Users },
        { id: "parents", label: "Родители", icon: Heart },
        { id: "private", label: "Личные", icon: User }
      ];
    }
    return [
      { id: "groups", label: "Группы", icon: Users },
      { id: "private", label: "Личные", icon: User }
    ];
  };

  const chatModes = getChatModes();

  const handleGroupSelect = (group) => {
    setSelectedGroup(group);
  };

  const handleBackFromGroup = () => {
    setSelectedGroup(null);
  };

  // If viewing a group chat, show GroupChat component
  if (selectedGroup) {
    return <GroupChat group={selectedGroup} user={user} onBack={handleBackFromGroup} />;
  }

  return (
    <div data-testid="chat-tab">
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-4">
        {chatModes.map((mode) => {
          const Icon = mode.icon;
          return (
            <Button
              key={mode.id}
              onClick={() => {
                setChatMode(mode.id);
                setLoading(true);
              }}
              variant={chatMode === mode.id ? "default" : "outline"}
              className={`flex-1 h-10 rounded-full ${chatMode === mode.id ? "bg-golf-green hover:bg-golf-green-hover" : ""}`}
              data-testid={`${mode.id}-chat-btn`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {mode.label}
            </Button>
          );
        })}
      </div>

      {chatMode === "groups" ? (
        <GroupsManager user={user} onGroupSelect={handleGroupSelect} />
      ) : chatMode === "private" ? (
        <div className="space-y-2">
          {/* Search Input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input 
              className="pl-9 h-10 bg-apple-gray border-transparent rounded-xl" 
              placeholder="Поиск..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {users.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Users className="w-8 h-8 text-apple-text-tertiary" />
              </div>
              <p className="text-apple-text-secondary">
                {isParent ? "Нет тренеров" : "Нет пользователей"}
              </p>
            </div>
          ) : (
            users
              .filter(u => 
                u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                u.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((u) => (
              <button
                key={u.user_id}
                onClick={() => navigate(`/chat/${u.user_id}`)}
                className="w-full p-4 rounded-2xl bg-apple-gray hover:bg-gray-200 transition-colors flex items-center gap-3 text-left"
                data-testid={`user-${u.user_id}`}
              >
                <div className="w-12 h-12 rounded-full bg-golf-green/10 flex items-center justify-center">
                  <span className="text-golf-green font-semibold text-lg">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-black">{u.first_name} {u.last_name}</p>
                  <p className="text-sm text-apple-text-secondary capitalize">
                    {u.role === "coach" ? "Тренер" : 
                     u.role === "athlete" ? "Спортсмен" :
                     u.role === "parent" ? "Родитель" : "Любитель"}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col" style={{ height: "calc(100vh - 280px)" }}>
          {chatMode === "parents" && (
            <div className="mb-3 p-3 bg-orange-50 rounded-xl border border-orange-100">
              <p className="text-sm text-orange-700 flex items-center gap-2">
                <Heart className="w-4 h-4" />
                {isCoach ? "Чат с родителями спортсменов" : "Чат родителей и тренеров"}
              </p>
            </div>
          )}

          <div className="flex-1 overflow-y-auto space-y-3 mb-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <MessageSquare className="w-8 h-8 text-apple-text-tertiary" />
                </div>
                <p className="text-apple-text-secondary">Нет сообщений</p>
              </div>
            ) : (
              messages.map((msg) => (
                <MessageBubble
                  key={msg.message_id}
                  message={msg}
                  isOwn={msg.sender_id === user?.user_id}
                  onReaction={handleReaction}
                  onImageClick={setViewingImage}
                  onVideoClick={setViewingVideo}
                  onReply={handleReply}
                  onDelete={handleDelete}
                  currentUserId={user?.user_id}
                  showSender={true}
                />
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply Preview */}
          {replyTo && (
            <div className="mb-2 p-2 bg-apple-gray rounded-xl flex items-center gap-2">
              <Reply className="w-4 h-4 text-golf-green shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-golf-green font-medium">{replyTo.sender_name}</p>
                <p className="text-sm text-gray-600 truncate">{replyTo.content}</p>
              </div>
              <button onClick={() => setReplyTo(null)} className="p-1">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
          )}

          {/* Selected File Preview */}
          {selectedFile && (
            <div className="py-2 bg-apple-gray rounded-xl mb-2">
              <div className="relative inline-block ml-4">
                {selectedFile.type?.startsWith("video/") ? (
                  <video src={URL.createObjectURL(selectedFile)} className="h-20 rounded-lg" />
                ) : (
                  <img src={URL.createObjectURL(selectedFile)} alt="Preview" className="h-20 rounded-lg" />
                )}
                <button
                  onClick={() => setSelectedFile(null)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*,video/*"
              className="hidden"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full shrink-0"
              data-testid="attach-file-btn"
            >
              <Image className="w-5 h-5 text-golf-green" />
            </Button>
            
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
              placeholder="Сообщение..."
              className="flex-1 h-10 bg-apple-gray border-transparent rounded-full px-4"
              data-testid="message-input"
            />
            
            <Button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedFile) || uploading}
              size="icon"
              className="rounded-full bg-golf-green hover:bg-golf-green-hover shrink-0"
              data-testid="send-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {viewingImage && (
        <ImageViewer imageUrl={viewingImage} onClose={() => setViewingImage(null)} />
      )}
      
      {viewingVideo && (
        <VideoViewer videoUrl={viewingVideo} onClose={() => setViewingVideo(null)} />
      )}
    </div>
  );
}
